import { Injectable } from '@angular/core';
import { Observable, catchError, of, tap } from 'rxjs';
import { ConfigBasedAdminClient, ConfigBasedStructuralEntryDTO, CreateConfigBasedStructuralEntryDTO, UpdateConfigBasedStructuralEntryDTO } from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable({
  providedIn: 'root'
})
export class StructuralEntryDetailsService {
  public isBusy = false;

  constructor(
    private configBasedAdminClient: ConfigBasedAdminClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService) { }

  public reset(): void {
    this.isBusy = false;
  }

  public getEntry(entryId: number): Observable<ConfigBasedStructuralEntryDTO> {
    this.spinnerService.show();
    return this.configBasedAdminClient.getConfigBasedStructuralEntry(entryId).pipe(
      tap(() => {
        this.spinnerService.hide();
      }),
      catchError((error: any) => {
        this.spinnerService.hide();
        return of(null);
      })
    );
  }

  public createEntry(createEntryDTO: CreateConfigBasedStructuralEntryDTO): Observable<ConfigBasedStructuralEntryDTO> {
    this.spinnerService.show();
    this.isBusy = true;
    return this.configBasedAdminClient.createConfigBasedStructuralEntry(createEntryDTO)
      .pipe(
        tap(() => {
          this.isBusy = false;
          this.spinnerService.hide();
          this.toastService.showSuccess(
            'i18n.domain.administration.structural-entry-details.messages.add-title',
            'i18n.domain.administration.structural-entry-details.messages.add-success-message'
          );
        }),
        catchError((error: any) => {
          this.isBusy = false;
          this.spinnerService.hide();
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return of(error);
        })
      );
  }

  public updateEntry(updateEntryDTO: UpdateConfigBasedStructuralEntryDTO): Observable<any> {
    this.spinnerService.show();
    this.isBusy = true;
    return this.configBasedAdminClient.updateConfigBasedStructuralEntry(updateEntryDTO)
      .pipe(
        tap(() => {
          this.isBusy = false;
          this.spinnerService.hide();
          this.toastService.showSuccess(
            'i18n.domain.administration.structural-entry-details.messages.update-title',
            'i18n.domain.administration.structural-entry-details.messages.update-success-message'
          );
        }),
        catchError((error: any) => {
          this.isBusy = false;
          this.spinnerService.hide();
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.handleErrorResponse(error);
          }
          return of(error);
        })
      );
  }

  public deleteEntry(id: number): Observable<any> {
    this.isBusy = true;
    this.spinnerService.show();
    return this.configBasedAdminClient.deleteConfigBasedStructuralEntry(id)
      .pipe(
        tap(() => {
          this.isBusy = false;
          this.spinnerService.hide();
          this.toastService.showSuccess(
            'i18n.domain.administration.structural-entry-details.messages.delete-title',
            'i18n.domain.administration.structural-entry-details.messages.delete-success-message'
          );
        }),
        catchError((error: any) => {
          this.isBusy = false;
          this.spinnerService.hide();
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return of(error);
        })
      );
  }

  private handleErrorResponse(error: any) {
    if (error && error.response) {
      try {
        const errorObject = JSON.parse(error.response);
        if (errorObject.ErrorCode) {
          this.toastService.showError('i18n.errors.server-error.title',
            'i18n.domain.request-management.errors.' + errorObject.ErrorCode,
            null, ErrorType.serverError);
        }
        else {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message',
            null, ErrorType.serverError);
        }
      }
      catch
      {
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message',
          null, ErrorType.serverError);
      }
    }
    else {
      this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message',
        null, ErrorType.serverError);
    }
  }
}
